import type { WallState } from '@common/paywall/wall-exposure';
import { PAYWALL_VARIANTS, type PaywallFeature } from './paywall-variants';

/** #137 A1 — the scheduling wall pitches the plan the user's engine needs.
 * Today every wall pitches Basic. With the experiment on, a user who runs a server (gateway)
 * engine is shown the Pro paywall variant instead, 50/50 against today's wall.
 *
 * The plan rule matches the backend day-6 check-in offer (trial-checkin render.ts): a
 * CONNECTED server session means Pro, otherwise the extension means Basic. Keep the two in step.
 *
 * Ships OFF (WALL_PLAN_BY_ENGINE_ENABLED); localStorage `bt_wall_plan_by_engine=1` turns it on
 * for one browser (rig validation). Assignment is deterministic per user, like the paywall holdout. */
export const WALL_PLAN_BY_ENGINE_ENABLED = false;
export const WALL_PLAN_EXPERIMENT_KEY = 'wall_plan_by_engine_2026q4';

export type WallPlanArm = 'off' | 'control' | 'by_engine';

type SessionLike = { owner?: string | null; status?: string | null; deleted?: boolean | null } | undefined | null;

export function usesServerEngine(sessions: ReadonlyArray<SessionLike>, userId: string | undefined | null): boolean {
  return sessions.some((s) => !!s && s.status === 'connected' && !s.deleted && (!userId || !s.owner || s.owner === userId));
}

export function isWallPlanExperimentOn(): boolean {
  if (WALL_PLAN_BY_ENGINE_ENABLED) return true;
  try {
    return localStorage.getItem('bt_wall_plan_by_engine') === '1';
  } catch {
    return false;
  }
}

export function wallPlanArm(userId: string | undefined | null, enabled: boolean): WallPlanArm {
  if (!enabled || !userId) return 'off';
  const key = `${WALL_PLAN_EXPERIMENT_KEY}:${userId}`;
  let h = 5381;
  for (let i = 0; i < key.length; i++) h = ((h << 5) + h + key.charCodeAt(i)) | 0;
  return Math.abs(h) % 2 === 0 ? 'control' : 'by_engine';
}

/** The paywall variant to render for the scheduling wall. `gateway` is the existing Pro variant. */
export function wallFeatureFor(arm: WallPlanArm, serverEngine: boolean): PaywallFeature {
  return arm === 'by_engine' && serverEngine ? 'gateway' : 'scheduling';
}

/** Props for the wall-shown and checkout events: the arm and the plan the wall pitches, so every arm
 * has a denominator. Empty when the experiment is off, so event payloads are unchanged.
 * The soft offer/hint states render neither plan wall, so they log `wall_state` but no `wall_plan`. */
export function wallPlanEventProps(
  arm: WallPlanArm,
  serverEngine: boolean,
  wallState?: WallState,
): { plan_arm?: WallPlanArm; wall_plan?: string; server_engine?: boolean; wall_state?: WallState } {
  if (arm === 'off') return {};
  const shown = wallState === undefined || wallState === 'hard';
  return {
    plan_arm: arm,
    ...(shown ? { wall_plan: PAYWALL_VARIANTS[wallFeatureFor(arm, serverEngine)].plan } : {}),
    server_engine: serverEngine,
    ...(wallState ? { wall_state: wallState } : {}),
  };
}
